import React, { useCallback, useMemo } from "react";

import { map, findLastIndex } from "lodash/fp";

import Box, { BoxProps } from "@mui/material/Box";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import { Participant } from "@app/analytics/MeetingReport/types";
import { TalkTurnData } from "@app/analytics/types";
import { parseUTCDateTime } from "@app/utils/utils";

import { Row } from "@rui/Stack";

import { PlayButton } from "./styles";

const DefaultBar = styled(Box)(({ theme }) => ({
  height: "8px",
  backgroundColor: theme.palette.shades.neutral[100],
  borderRadius: "2px",
  position: "relative",
  minWidth: "50px",
  [theme.breakpoints.up("xs")]: {
    minWidth: "100px",
  },
  [theme.breakpoints.up("sm")]: {
    minWidth: "240px",
  },
  [theme.breakpoints.up("mobile")]: {
    minWidth: "130px",
  },
  [theme.breakpoints.up("lg")]: {
    minWidth: "240px",
  },
}));

const StyledScrubBar = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: 0,
  height: "8px",
  borderRadiusTopRight: "2px",
  borderRadiusBottomRight: "2px",
  backgroundColor: theme.palette.shades.neutral[300],
  zIndex: 1,
}));

const ScrubBar = ({
  meetingStartTime,
  meetingEndTime,
}: {
  meetingStartTime: number;
  meetingEndTime: number;
}) => {
  const currentTimestamp = useVideoStore((state) => state.currentTimestamp);
  const scrubBarWidth =
    currentTimestamp / ((meetingEndTime - meetingStartTime) / 1000);

  return <StyledScrubBar sx={{ width: scrubBarWidth }} />;
};

const TalkTime = styled(Typography)(({ theme }) => ({
  color: theme.palette.shades.neutral[700],
  width: "32px",
  textAlign: "right",
}));

type TimeChunkProps = BoxProps & { startPx: number; width: number };

const TimeChunk = styled<React.FC<TimeChunkProps>>(
  ({ startPx: _sp, width: _w, ...props }) => <Box {...props} />
)(({ theme, startPx, width }) => ({
  height: "8px",
  backgroundColor: theme.palette.shades.secondary[500],
  position: "absolute",
  left: `${startPx}%`,
  width: `${width}%`,
  zIndex: 2,
}));

interface TimeChunkData {
  /* percent offset from the left */
  startPx: number;
  width: number;
}

const getTimeChunks = (
  startTime: number,
  endTime: number,
  talkTurns: TalkTurnData[]
): TimeChunkData[] => {
  const totalDuration = endTime - startTime;

  return map((talkTurn) => {
    const turnStartTime =
      typeof talkTurn.startTime === "number"
        ? talkTurn.startTime
        : parseUTCDateTime(talkTurn.startTime).toMillis();

    const turnEndTime =
      typeof talkTurn.endTime === "number"
        ? talkTurn.endTime
        : parseUTCDateTime(talkTurn.endTime).toMillis();

    return {
      startPx: ((turnStartTime - startTime) / totalDuration) * 100,
      width: ((turnEndTime - turnStartTime) / totalDuration) * 100,
    };
  }, talkTurns);
};

const TalkTimeBar = ({
  participant,
  meetingStartTime,
  meetingEndTime,
  onPlayToggle,
  onTurnClick,
  playing,
  isReelSelected,
  playable,
}: {
  participant: Participant;
  meetingStartTime: number;
  meetingEndTime: number;
  onPlayToggle: (play: boolean) => void;
  onTurnClick: (index: number) => void;
  playing: boolean;
  isReelSelected: boolean;
  playable: boolean;
}) => {
  const rawTalkTimePercent = participant.talkTimeData.talkTimePercent;
  const formattedTalkTimePercent =
    rawTalkTimePercent > 0 && rawTalkTimePercent < 1
      ? "<1"
      : Math.round(rawTalkTimePercent);

  const handlePlayClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onPlayToggle(!playing);
    },
    [playing]
  );

  const timeChunks = useMemo(
    () =>
      getTimeChunks(
        meetingStartTime,
        meetingEndTime,
        participant.talkTimeData.talkTurns ?? []
      ),
    [meetingStartTime, meetingEndTime, participant.talkTimeData.talkTurns]
  );

  const handleBarClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isReelSelected) return;

      const boundingBox = event.currentTarget.getBoundingClientRect();
      const startPx =
        ((event.clientX - boundingBox.left) / boundingBox.width) * 100;
      const closestBeforeIndex = findLastIndex(
        (chunk) => chunk.startPx < startPx,
        timeChunks
      );

      const previousChunk = timeChunks[closestBeforeIndex];
      // We clicked in the middle of chunk,
      // start playing at the beginning of the chunk
      if (
        previousChunk &&
        startPx < previousChunk.startPx + previousChunk.width
      ) {
        onTurnClick(closestBeforeIndex);
        return;
      }

      // else we clicked between chunks so choose the next one or
      // the last chunk, play from the beginning
      const chunkIndex = Math.min(
        closestBeforeIndex + 1,
        timeChunks.length - 1
      );
      onTurnClick(chunkIndex);
    },
    [isReelSelected, timeChunks]
  );

  return (
    <Row variant="right" spacing={1} flex={1}>
      {playable && timeChunks.length > 0 && (
        <PlayButton playing={playing} shade="light" onClick={handlePlayClick} />
      )}
      <DefaultBar onClick={handleBarClick}>
        {isReelSelected && (
          <ScrubBar
            meetingStartTime={meetingStartTime}
            meetingEndTime={meetingEndTime}
          />
        )}
        {timeChunks.map((timeChunk) => (
          <TimeChunk
            key={timeChunk.startPx}
            startPx={timeChunk.startPx}
            width={timeChunk.width}
          />
        ))}
      </DefaultBar>
      <TalkTime variant="bodySecondaryReg">
        {formattedTalkTimePercent}%
      </TalkTime>
    </Row>
  );
};

export default TalkTimeBar;
